import { type Common } from '../common.js';
import { AgeGroup, IncomeGroup, MaritalStatus } from '../models/common.js';
import { FeedbackType } from '../models/feedback.js';
import { Ethnicity, Race, Sex } from '../models/patient.js';
import {
  type ChatMessageType,
  type Conversion,
  type Feedback,
  type Filters,
  Platform,
  type Query,
  type Satisfaction,
} from '../models/report/feedback.js';

export class API {
  common: Common;
  BASE_URL = `/analytics/previsit-geospatial-conversion`;

  constructor(common: Common) {
    this.common = common;
  }

  async chatCompletion(messages: ChatMessageType[]): Promise<{ answer: string }> {
    const url = this.common.constructPath(`${this.BASE_URL}/chat-completion`);
    return await this.common.postJSON(url, { messages });
  }

  async getFeedback(query: Query): Promise<Feedback[]> {
    const url = this.common.constructPath(`${this.BASE_URL}/feedback`, constructQuery(query));
    const { data } = await this.common.getJSON(url);
    return data;
  }

  async getSatisfaction(query: Query): Promise<Satisfaction[]> {
    const url = this.common.constructPath(
      `${this.BASE_URL}/feedback-daily-counts`,
      constructQuery(query)
    );
    const { data } = await this.common.getJSON(url);
    return data;
  }

  async getConversion(query: Query): Promise<Conversion[]> {
    const url = this.common.constructPath(
      `${this.BASE_URL}/intake-completion-daily-counts`,
      constructQuery(query)
    );
    const { data } = await this.common.getJSON(url);
    return data;
  }
}

function constructQuery(query: Query) {
  return {
    startDate: query.startDate,
    endDate: query.endDate,
    ...constructFilterToQuery(query.filters),
  };
}

function normalize(value: string) {
  return value.toUpperCase();
}

function constructFilterToQuery(filters: Filters) {
  const filtersToAdd = new Set(
    Object.entries(filters)
      .filter(([, value]) => value)
      .map(([key]) => normalize(key))
  );
  function enumFilter(enumObj: object) {
    return Object.entries(enumObj)
      .filter(([key]) => filtersToAdd.has(normalize(key).replace(/[^A-Za-z0-9]/g, '')))
      .map(([, value]) => value);
  }
  return {
    ageGroup: enumFilter(AgeGroup),
    gender: enumFilter(Sex),
    hispanicOrLatino: enumFilter(Ethnicity),
    incomeGroup: enumFilter(IncomeGroup),
    maritalStatus: enumFilter(MaritalStatus),
    platform: enumFilter(Platform),
    race: enumFilter(Race),
    sentiment: enumFilter(FeedbackType),
    organizationName: filters.organizationName,
  };
}
